import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Alert,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { clearMustChangePassword, logoutUser } from '../store/authSlice';
import { changeOwnPassword } from '../services/termsService';
import { getPasswordRuleError, PASSWORD_RULE_HINT } from '../utils/passwordValidation';

/**
 * Blocking forced-password-change gate. Shown once the EUTC gate is satisfied
 * (or immediately if the EUTC was already accepted) while
 * termsStatus.mustChangePassword === true. A missing termsStatus (older
 * backend) means no gate. Changing the password does not invalidate the
 * current Firebase session, so no re-login is needed.
 */
const ForcePasswordChangeDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const termsStatus = useAppSelector((state) => state.auth?.termsStatus);

  // Defer to the EUTC gate: only open once acceptance is no longer pending
  const isGateOpen =
    termsStatus?.mustChangePassword === true && termsStatus?.eutcAccepted !== false;

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const validateForm = (): boolean => {
    const ruleError = getPasswordRuleError(newPassword);
    const mismatchError =
      newPassword !== confirmPassword ? 'Passwords do not match' : null;

    setNewPasswordError(ruleError);
    setConfirmPasswordError(mismatchError);
    return !ruleError && !mismatchError;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      await changeOwnPassword(newPassword);
      // Clear the flag locally so the gate closes without a reload; the
      // current Firebase session stays valid.
      dispatch(clearMustChangePassword());
    } catch (changeError: any) {
      console.error('[ForcePasswordChangeDialog] Failed to change password:', changeError);
      setSubmitError(changeError?.message || 'Failed to set your new password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await dispatch(logoutUser());
    setLoggingOut(false);
  };

  if (!isGateOpen) {
    return null;
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      aria-labelledby="force-password-change-title"
      // No onClose: the gate must not be dismissable via backdrop or Escape
    >
      <DialogTitle id="force-password-change-title">Set your new password</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          For security, you must set a new password before continuing.
        </Typography>

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setNewPasswordError(null);
            }}
            fullWidth
            required
            autoFocus
            inputProps={{ autoComplete: 'new-password' }}
            error={!!newPasswordError}
            helperText={newPasswordError || PASSWORD_RULE_HINT}
            disabled={submitting}
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setConfirmPasswordError(null);
            }}
            fullWidth
            required
            inputProps={{ autoComplete: 'new-password' }}
            error={!!confirmPasswordError}
            helperText={confirmPasswordError || 'Re-enter the new password'}
            disabled={submitting}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout} color="error" disabled={submitting || loggingOut}>
          Log out
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || loggingOut || !newPassword || !confirmPassword}
          startIcon={submitting ? <CircularProgress size={18} /> : null}
        >
          {submitting ? 'Saving...' : 'Set Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForcePasswordChangeDialog;
